import type { WeekScheduleCell } from "../../../pages/Profile/v2/WeeklySchedule/weekScheduleCellResolver";
import { formatHoursMinutes, formatPtoTimeRange, isFullStaffingDayPto } from "../../../utils/dateUtils";
import type { BaseNurseShift } from "../../../utils/models";
import type { DayEntry, WeekStaffScheduleContentToggles, WeekStaffScheduleDetailToggles, WeekStaffScheduleExportLabels } from "../types";

type BuildDayEntriesArgs = {
	cell: WeekScheduleCell;
	content: WeekStaffScheduleContentToggles;
	details: WeekStaffScheduleDetailToggles;
	labels: WeekStaffScheduleExportLabels;
	dayStart: string;
	/** Job names are only meaningful when the employee holds more than one job, matching the grid. */
	hasMultipleJobs: boolean;
};

/** Same wording as the on-screen shift card. */
const shiftTimeLabel = (shift: BaseNurseShift): string => `${shift.startTime.toFormat("h:mm a")} - ${shift.endTime.toFormat("h:mm a")}`;

/**
 * Flatten one day cell into the entries the sheet renders, in the same top-to-bottom order the
 * on-screen cell uses: time off, then actual shifts, then scheduled shifts, then a preference.
 *
 * A preference is only shown on an otherwise-empty day — same rule as the grid cell, which hides the
 * "prefers" card as soon as anything real occupies the day.
 */
export const buildDayEntries = ({ cell, content, details, labels, dayStart, hasMultipleJobs }: BuildDayEntriesArgs): DayEntry[] => {
	const entries: DayEntry[] = [];
	const detailOf = (shift: BaseNurseShift) => ({
		location: details.includeLocation ? shift.location : undefined,
		jobName: details.includeJobName && hasMultipleJobs ? shift.jobName : undefined,
	});

	if (content.includeTimeOff && cell.dayOff) {
		const { dateFrom, dateTo, hours } = cell.dayOff;
		entries.push({
			kind: "pto",
			id: cell.dayOff.id,
			timeOffLabel: labels.timeOff,
			// Partial-day time off shows its window; a full staffing day shows the hours count.
			amountLabel: isFullStaffingDayPto(dateFrom, dateTo, dayStart) ? formatHoursMinutes(hours) : formatPtoTimeRange(dateFrom, dateTo),
		});
	}

	if (content.includeActual) {
		for (const shift of cell.actualShifts) {
			entries.push({ kind: "actual", id: shift.id, timeLabel: shiftTimeLabel(shift), ...detailOf(shift) });
		}
	}

	if (content.includeScheduled) {
		for (const shift of cell.scheduledShifts ?? []) {
			entries.push({ kind: "scheduled", id: shift.id, timeLabel: shiftTimeLabel(shift), ...detailOf(shift) });
		}
	}

	// Gated on the cell itself, not on `entries` — switching a content toggle off must not surface a
	// preference the on-screen cell never showed.
	const dayIsOtherwiseEmpty = !cell.scheduledShifts?.length && !cell.actualShifts.length && !cell.dayOff;
	if (content.includePreferences && cell.dayPreference && dayIsOtherwiseEmpty) {
		const { id, start_date, end_date } = cell.dayPreference;
		entries.push({ kind: "preference", id, prefersLabel: labels.prefers, timeLabel: `${start_date.toFormat("h:mm a")} - ${end_date.toFormat("h:mm a")}` });
	}

	return entries;
};
